import { getImageProps } from 'next/image'
import { useTranslation } from '@mc/i18n'
import { cloudflareLoader } from '@mc/image-cloudflare'
import MasterclassCertificatesWhite from 'pages/certificates/assets/MasterclassCertificatesWhite.svg'
import type {
  CohortConfig,
  CohortContent,
  CohortImage,
  EnrollmentStateResult,
  Responsive,
} from '../types'
import { CohortFactBar } from './CohortFactBar'
import { CohortStateBlock } from './CohortStateBlock'
import { CohortTrailerButton } from './CohortTrailerButton'
import { ResponsiveText } from './ResponsiveText'
import { getHeroFacts } from './getHeroFacts'
import { getStateCopy } from './getStateCopy'

type CohortHeroProps = {
  config: CohortConfig
  content: CohortContent
  enrollment: EnrollmentStateResult
  onNotify?: () => void
}

const byBreakpoint = (image: Responsive<CohortImage>) =>
  'url' in image ? { mobile: image, desktop: image } : image

export const CohortHero = ({
  config,
  content,
  enrollment,
  onNotify,
}: CohortHeroProps) => {
  const { t } = useTranslation('@mc/certificates')
  const { hero } = content
  const image = byBreakpoint(hero.image)
  const imageProps = {
    fill: true,
    sizes: '100vw',
    loader: cloudflareLoader,
    fetchPriority: 'high',
    loading: 'eager',
  } as const
  const { props: desktopImage } = getImageProps({
    ...imageProps,
    src: image.desktop.url,
    alt: image.desktop.alt,
  })
  const { props: mobileImage } = getImageProps({
    ...imageProps,
    src: image.mobile.url,
    alt: image.mobile.alt,
  })
  const facts = getHeroFacts(config, content, (count) =>
    t('marketingPages.cohorts.hero.durationWeeks', { count }),
  )

  return (
    <section className='relative overflow-hidden bg-c-base-1400 text-c-text-light'>
      <div className='absolute inset-x-0 top-0 aspect-square lg:inset-0 lg:aspect-auto'>
        <picture>
          <source
            media='(min-width: 960px)'
            srcSet={desktopImage.srcSet}
            sizes={desktopImage.sizes}
          />
          <img
            {...mobileImage}
            alt={mobileImage.alt}
            className='object-cover'
          />
        </picture>
        <div className='absolute inset-0 bg-gradient-to-b from-transparent via-c-base-1400/40 to-c-base-1400 lg:bg-gradient-to-r lg:from-c-base-1400/90 lg:via-c-base-1400/40 lg:to-transparent' />
      </div>

      {hero.trailerMediaUuid && (
        <div className='container absolute inset-x-0 top-5 z-20 flex justify-end'>
          <CohortTrailerButton
            mediaUuid={hero.trailerMediaUuid}
            contentId={config.slug}
            contentTitle={config.name}
          />
        </div>
      )}

      <div className='container relative z-10 flex flex-col pb-7 pt-11 lg:pt-10'>
        <div className='flex flex-col gap-5 lg:w-5/12'>
          <MasterclassCertificatesWhite
            role='img'
            aria-label='MasterClass Certificates'
            className='h-[44px] w-[160px]'
          />
          <p className='c-body-bold m-0'>{hero.eyebrow}</p>
          <h1 className='c-heading-1 c-text-ivar-display-condensed m-0'>
            <ResponsiveText value={hero.headline} />
          </h1>
          <p className='c-body m-0'>{hero.subhead}</p>
        </div>

        <div className='mt-6 flex flex-col gap-6 rounded-sm bg-c-beige-100/10 p-5 backdrop-blur lg:flex-row lg:items-center lg:justify-between md:p-4 lg:gap-4 lg:rounded-lg'>
          <CohortFactBar facts={facts} />
          <CohortStateBlock
            copy={getStateCopy(content, config, enrollment)}
            joinLabel={hero.ctaLabel}
            onNotify={onNotify}
          />
        </div>
      </div>
    </section>
  )
}
